/**
 * WorkflowAutomationCard — renders a single automation/cron job card.
 */

import type { CronJob } from "./types";
import styles from "./workflow.module.scss";

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface Props {
  job: CronJob;
  agentMap: Map<string, string>;
}

export const WorkflowAutomationCard = ({ job, agentMap }: Props) => {
  return (
    <div
      className={`${styles.automationCard} ${!job.enabled ? styles.automationCardDisabled : ""}`}
    >
      {/* Card header with name, schedule, badges */}
      <div className={styles.automationCardHeader}>
        <div className={styles.automationCardInfo}>
          <h4 className={styles.automationCardName}>{job.name}</h4>
          <span className={styles.automationCardSchedule}>
            {job.schedule_human ?? job.schedule}
          </span>
        </div>
        <div className={styles.automationCardBadges}>
          <span
            className={`${styles.automationEnabledBadge} ${job.enabled ? styles.badgeEnabled : styles.badgeDisabled}`}
          >
            {job.enabled ? "Active" : "Disabled"}
          </span>
          {job.last_status && (
            <span className={styles.automationStatusBadge} data-status={job.last_status}>
              {job.last_status}
            </span>
          )}
        </div>
      </div>

      {/* Card body with detail grid */}
      <div className={styles.automationCardBody}>
        <div className={styles.automationCardDetails}>
          <div className={styles.automationDetail}>
            <span className={styles.automationDetailLabel}>Next run</span>
            <span className={styles.automationDetailValue}>
              {job.next_run ? formatDateTime(job.next_run) : "--"}
            </span>
          </div>
          <div className={styles.automationDetail}>
            <span className={styles.automationDetailLabel}>Last run</span>
            <span className={styles.automationDetailValue}>
              {job.last_run_at ? formatDateTime(job.last_run_at) : "--"}
            </span>
          </div>
          {job.agent_id && (
            <div className={styles.automationDetail}>
              <span className={styles.automationDetailLabel}>Agent</span>
              <span
                className={styles.automationDetailValue}
                title={agentMap.get(job.agent_id) ?? job.agent_id}
              >
                {agentMap.get(job.agent_id) ?? job.agent_id}
              </span>
            </div>
          )}
          <div className={styles.automationDetail}>
            <span className={styles.automationDetailLabel}>Cron</span>
            <span className={styles.automationDetailValue}>
              <code className={styles.automationCronExpr}>{job.schedule}</code>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
